/**
 * DetallePage — Detalle de un charter con timeline de estados y acciones.
 * Endpoints:
 *   GET   /turismo/charter/reservas/{id}
 *   PATCH /turismo/charter/reservas/{id}/estado
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Chip, Divider, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, InputAdornment,
  Snackbar, List, ListItem, ListItemText, IconButton,
} from '@mui/material';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import FlagIcon from '@mui/icons-material/Flag';
import CancelIcon from '@mui/icons-material/Cancel';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EditIcon from '@mui/icons-material/Edit';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getFirebaseAuthHeaders } from '../common/firebaseAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

async function getAuthHeaders() { return getFirebaseAuthHeaders(); }
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const ESTADO_INFO = {
  presupuesto: { label: 'Presupuesto', bg: '#fef3c7', fg: '#92400e' },
  confirmada:  { label: 'Confirmada',  bg: '#dbeafe', fg: '#1e40af' },
  embarcada:   { label: 'Embarcada',   bg: '#dcfce7', fg: '#166534' },
  completada:  { label: 'Completada',  bg: '#e2e8f0', fg: '#334155' },
  cancelada:   { label: 'Cancelada',   bg: '#fee2e2', fg: '#991b1b' },
};

// Transiciones válidas (espejo del backend TRANSICIONES)
const ACCIONES = {
  presupuesto: [
    { estado: 'confirmada', label: 'Confirmar',  color: '#1e40af', icon: <CheckCircleIcon fontSize="small" /> },
    { estado: 'cancelada',  label: 'Cancelar',   color: '#991b1b', icon: <CancelIcon fontSize="small" /> },
  ],
  confirmada: [
    { estado: 'embarcada',  label: 'Embarcar',   color: '#166534', icon: <DirectionsBoatIcon fontSize="small" /> },
    { estado: 'cancelada',  label: 'Cancelar',   color: '#991b1b', icon: <CancelIcon fontSize="small" /> },
  ],
  embarcada: [
    { estado: 'completada', label: 'Completar',  color: '#334155', icon: <DoneAllIcon fontSize="small" /> },
  ],
  completada: [],
  cancelada: [],
};

export default function DetallePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { empresaId, refreshCounts } = useOutletContext() || {};
  const [charter, setCharter] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, accion: null });
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [cobroOpen, setCobroOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [embarcaciones, setEmbarcaciones] = useState([]);
  const [puntos, setPuntos] = useState([]);
  const [auditoria, setAuditoria] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [rCh, rPag, rAud] = await Promise.all([
        fetch(`${API_BASE}/turismo/charter/reservas/${id}?empresa_id=${empresaId}`,
          { headers: await getAuthHeaders() }),
        fetch(`${API_BASE}/pagos/reservacion/${id}`, { headers: await getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/auditoria?empresa_id=${empresaId}&entidad_tipo=reservacion&entidad_id=${id}&limit=100`,
          { headers: await getAuthHeaders() }),
      ]);
      if (rCh.ok) setCharter(await rCh.json());
      else setCharter(null);
      if (rPag.ok) {
        const data = await rPag.json();
        setPagos(Array.isArray(data) ? data : (data.pagos || []));
      }
      if (rAud.ok) {
        const data = await rAud.json();
        setAuditoria(data.items || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id, empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Precarga embarcaciones + puntos para el diálogo de edición.
  // useEffect no puede ser async — usamos IIFE.
  useEffect(() => {
    if (!empresaId) return;
    (async () => {
      const headers = await getAuthHeaders();
      fetch(`${API_BASE}/turismo/embarcaciones?empresa_id=${empresaId}`, { headers })
        .then(r => r.ok ? r.json() : [])
        .then(setEmbarcaciones)
        .catch(() => {});
      fetch(`${API_BASE}/turismo/puntos-embarque?empresa_id=${empresaId}`, { headers })
        .then(r => r.ok ? r.json() : [])
        .then(setPuntos)
        .catch(() => {});
    })();
  }, [empresaId]);

  // Deep-link ?open=cobrar → abre diálogo directamente (usado desde muelle)
  useEffect(() => {
    if (searchParams.get('open') === 'cobrar' && charter && charter.saldo_pendiente > 0) {
      setCobroOpen(true);
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [charter, searchParams, setSearchParams]);

  const abrirDialog = (accion) => {
    setMotivo('');
    setDialog({ open: true, accion });
  };

  const confirmarCambio = async () => {
    if (!dialog.accion || saving) return;
    setSaving(true);
    try {
      const body = { estado: dialog.accion.estado };
      if (motivo.trim()) body.motivo = motivo.trim();
      const r = await fetch(
        `${API_BASE}/turismo/charter/reservas/${id}/estado?empresa_id=${empresaId}`,
        { method: 'PATCH', headers: await getAuthHeaders(), body: JSON.stringify(body) }
      );
      const data = await r.json();
      if (!r.ok) {
        const msg = data?.detail?.message || data?.detail || 'Error al cambiar estado';
        setSnack({ open: true, msg: typeof msg === 'string' ? msg : JSON.stringify(msg), severity: 'error' });
        return;
      }
      setSnack({ open: true, msg: `Estado actualizado a ${dialog.accion.label}`, severity: 'success' });
      setDialog({ open: false, accion: null });
      cargar();
      refreshCounts?.();
    } catch (e) {
      console.error(e);
      setSnack({ open: true, msg: 'Error de red', severity: 'error' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#7c3aed' }} />
      </Box>
    );
  }
  if (!charter) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ color: '#64748b', mb: 2 }}>Charter no encontrado.</Typography>
        <Button variant="outlined" onClick={() => navigate('/charter/listado')}>Volver al listado</Button>
      </Paper>
    );
  }

  const est = ESTADO_INFO[charter.estado] || ESTADO_INFO.presupuesto;
  const acciones = ACCIONES[charter.estado] || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/charter/listado')}
          sx={{ textTransform: 'none', color: '#64748b' }}>
          Volver
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
            Charter <span style={{ fontFamily: 'monospace', color: '#7c3aed' }}>{charter.codigo_reserva}</span>
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            {charter.cliente?.nombre} · {charter.embarcacion_nombre} · {fmtDate(charter.fecha_alquiler)}
          </Typography>
        </Box>
        {!['completada', 'cancelada'].includes(charter.estado) && (
          <Button variant="outlined" startIcon={<EditIcon />}
            onClick={() => setEditOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 700,
              color: '#334155', borderColor: '#cbd5e1',
              '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' },
            }}
          >
            Editar
          </Button>
        )}
        {charter.cliente?.whatsapp && (
          <Button variant="outlined" startIcon={<WhatsAppIcon />}
            onClick={() => {
              const num = String(charter.cliente.whatsapp).replace(/[^0-9]/g, '');
              const msg = buildWhatsAppMessage(charter, empresaId);
              window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
            }}
            sx={{
              textTransform: 'none', fontWeight: 700,
              color: '#16a34a', borderColor: '#16a34a',
              '&:hover': { bgcolor: '#f0fdf4' },
            }}
          >
            WhatsApp
          </Button>
        )}
        <Button
          startIcon={<PictureAsPdfIcon />}
          onClick={() => window.open(
            `${API_BASE}/turismo/charter/reservas/${charter.id}/comprobante.pdf?empresa_id=${empresaId}`,
            '_blank'
          )}
          sx={{
            textTransform: 'none', fontWeight: 700,
            color: '#7c3aed', borderColor: '#7c3aed',
            '&:hover': { bgcolor: '#f5f3ff' },
          }}
          variant="outlined"
        >
          Comprobante PDF
        </Button>
        <Chip label={est.label} sx={{ bgcolor: est.bg, color: est.fg, fontWeight: 700, fontSize: 13, px: 1 }} />
      </Box>

      <Grid container spacing={2}>
        {/* Datos del cliente */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Cliente
            </Typography>
            <Row label="Nombre"   value={charter.cliente?.nombre} />
            <Row label="WhatsApp" value={charter.cliente?.whatsapp} />
            <Row label="Correo"   value={charter.cliente?.correo || '—'} />
            <Row label="ID"       value={charter.cliente?.identificacion || '—'} />
          </Paper>
        </Grid>

        {/* Servicio */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Servicio
            </Typography>
            <Row label="Catálogo"    value={charter.alquiler_nombre || `#${charter.alquiler_id}`} />
            <Row label="Embarcación" value={`${charter.embarcacion_nombre} · ${charter.embarcacion_capacidad || '?'} pax`} />
            <Row label="Fecha"       value={fmtDate(charter.fecha_alquiler)} />
            <Row label="Zarpe est."  value={charter.hora_zarpe || '—'} />
            {charter.hora_zarpe_real && (
              <Row label="Zarpe real"
                value={new Date(charter.hora_zarpe_real).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false })}
                valueColor="#059669"
              />
            )}
            <Row label="Regreso est." value={charter.hora_regreso || '—'} />
            {charter.hora_regreso_real && (
              <Row label="Regreso real"
                value={new Date(charter.hora_regreso_real).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false })}
                valueColor="#059669"
              />
            )}
            <Row label="Grupo"       value={`${charter.adultos} adultos · ${charter.ninos} niños · ${charter.bebes} bebés (${charter.pax_total})`} />
            <Row label="Muelle"
              value={charter.punto_embarque_nombre
                ? `${charter.punto_embarque_nombre}${charter.punto_embarque_direccion ? ` · ${charter.punto_embarque_direccion}` : ''}`
                : 'Sin definir'}
              valueColor={charter.punto_embarque_nombre ? undefined : '#94a3b8'}
            />
          </Paper>
        </Grid>

        {/* Financiero */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Financiero
              </Typography>
              {charter.saldo_pendiente > 0 && ['confirmada', 'embarcada'].includes(charter.estado) && (
                <Button size="small" variant="contained" startIcon={<PaymentIcon />}
                  onClick={() => setCobroOpen(true)}
                  sx={{
                    textTransform: 'none', fontWeight: 700,
                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                    boxShadow: '0 2px 8px rgba(22, 163, 74, 0.35)',
                    '&:hover': { background: 'linear-gradient(135deg, #15803d 0%, #14532d 100%)' },
                  }}
                >
                  Cobrar
                </Button>
              )}
            </Box>
            {charter.descuento_comercial > 0 && (
              <>
                <Row label="Precio catálogo" value={fmtMoney(charter.precio_base_catalogo)} />
                <Row
                  label="Descuento"
                  value={`− ${fmtMoney(charter.descuento_comercial)}`}
                  valueColor="#dc2626"
                />
                {charter.descuento_comercial_motivo && (
                  <Row label="Motivo" value={charter.descuento_comercial_motivo} />
                )}
              </>
            )}
            <Row label="Precio total" value={fmtMoney(charter.precio_total)} />
            <Row label="Total pagado"
              value={fmtMoney(pagos.reduce((s, p) => s + Number(p.monto || 0), 0))}
              valueColor="#059669"
            />
            <Row label="Saldo pendiente"
              value={fmtMoney(charter.saldo_pendiente)}
              valueColor={charter.saldo_pendiente > 0 ? '#dc2626' : '#059669'}
            />
            <Row label="Estado embarque" value={charter.estado_embarque || '—'} />
            {charter.estado === 'confirmada' && charter.saldo_pendiente > 0 && (
              <Alert severity="warning" sx={{ mt: 1, fontSize: 12 }}>
                No podrá embarcar hasta que el saldo esté en cero.
              </Alert>
            )}
            {pagos.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                  Pagos registrados
                </Typography>
                <List dense sx={{ py: 0 }}>
                  {pagos.map(p => (
                    <ListItem key={p.id} sx={{ py: 0.5, px: 0 }}>
                      <ReceiptLongIcon sx={{ fontSize: 16, color: '#94a3b8', mr: 1 }} />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                              {p.metodo_pago || p.origen}
                            </Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                              {fmtMoney(p.monto)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                            {p.created_at ? new Date(p.created_at).toLocaleString('es-CO') : ''}
                            {p.notas ? ` · ${p.notas}` : ''}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Paper>
        </Grid>

        {/* Notas y motivo */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Notas
            </Typography>
            <Typography sx={{ fontSize: 13, color: charter.notas ? '#0f172a' : '#94a3b8', whiteSpace: 'pre-wrap' }}>
              {charter.notas || 'Sin notas'}
            </Typography>
            {charter.motivo_cancelacion && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography sx={{ fontSize: 12, color: '#991b1b', fontWeight: 700 }}>
                  Motivo cancelación:
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#991b1b' }}>
                  {charter.motivo_cancelacion}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Historial de auditoría */}
      {auditoria.length > 0 && (
        <Paper sx={{ p: 2.5, mt: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Historial
          </Typography>
          <TimelineHistorial eventos={auditoria} />
        </Paper>
      )}

      {/* Acciones */}
      {acciones.length > 0 && (
        <Paper sx={{ p: 2.5, mt: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Acciones disponibles
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {acciones.map(a => (
              <Button key={a.estado} variant="outlined" startIcon={a.icon}
                onClick={() => abrirDialog(a)}
                sx={{
                  textTransform: 'none', fontWeight: 700,
                  color: a.color, borderColor: a.color,
                  '&:hover': { bgcolor: `${a.color}10`, borderColor: a.color },
                }}
              >
                {a.label}
              </Button>
            ))}
          </Box>
        </Paper>
      )}

      {/* Dialog confirmación */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, accion: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirmar: {dialog.accion?.label}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#64748b', mb: 2 }}>
            Vas a cambiar el estado de <strong>{charter.codigo_reserva}</strong> a
            <strong> {dialog.accion?.label}</strong>.
          </Typography>
          {dialog.accion?.estado === 'cancelada' && (
            <>
              {pagos.length > 0 && (
                <Alert severity="warning" sx={{ fontSize: 12, mb: 2 }}>
                  Esta reserva tiene <strong>{pagos.length} pago{pagos.length > 1 ? 's' : ''} registrado{pagos.length > 1 ? 's' : ''}</strong>
                  {' '}por un total de {fmtMoney(pagos.reduce((s, p) => s + Number(p.monto || 0), 0))}.
                  Los pagos NO se eliminan; deberás procesar reintegro manualmente si aplica.
                </Alert>
              )}
              <TextField
                fullWidth size="small" multiline rows={3} required
                label="Motivo de cancelación (obligatorio)"
                value={motivo} onChange={(e) => setMotivo(e.target.value)}
                error={!motivo.trim()}
                helperText={!motivo.trim() ? 'Debes indicar por qué se cancela' : ' '}
              />
            </>
          )}
          {dialog.accion?.estado === 'confirmada' && (
            <Alert severity="info" sx={{ fontSize: 12 }}>
              Al confirmar se valida anti-overbooking. Si otra reserva tomó la embarcación
              mientras negociabas, recibirás error 409.
            </Alert>
          )}
          {dialog.accion?.estado === 'embarcada' && (
            <Alert severity="warning" sx={{ fontSize: 12 }}>
              El saldo debe estar en cero para embarcar. Se registrará la hora real de zarpe.
            </Alert>
          )}
          {dialog.accion?.estado === 'completada' && (
            <Alert severity="info" sx={{ fontSize: 12 }}>
              Se registrará la hora real de regreso ahora.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, accion: null })} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={confirmarCambio}
            disabled={saving || (dialog.accion?.estado === 'cancelada' && !motivo.trim())}
            sx={{
              textTransform: 'none', fontWeight: 700,
              bgcolor: dialog.accion?.color || '#7c3aed',
              '&:hover': { bgcolor: dialog.accion?.color || '#6d28d9' },
            }}
          >
            {saving ? 'Guardando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <CobroCharterDialog
        open={cobroOpen}
        charter={charter}
        empresaId={empresaId}
        onClose={() => setCobroOpen(false)}
        onSuccess={(msg) => {
          setCobroOpen(false);
          setSnack({ open: true, msg, severity: 'success' });
          cargar();
          refreshCounts?.();
        }}
        onError={(msg) => setSnack({ open: true, msg, severity: 'error' })}
      />

      <EditarCharterDialog
        open={editOpen}
        charter={charter}
        embarcaciones={embarcaciones}
        puntos={puntos}
        empresaId={empresaId}
        onClose={() => setEditOpen(false)}
        onSuccess={(msg) => {
          setEditOpen(false);
          setSnack({ open: true, msg, severity: 'success' });
          cargar();
          refreshCounts?.();
        }}
        onError={(msg) => setSnack({ open: true, msg, severity: 'error' })}
      />

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/**
 * CobroCharterDialog — diálogo simple para registrar un cobro contra el charter.
 * Reutiliza el endpoint PATCH /turismo/muelle/reservas/{id}/cobrar (mismo que
 * usa el muelle para pasadías). El método técnico se deriva de la cuenta destino.
 */
function CobroCharterDialog({ open, charter, empresaId, onClose, onSuccess, onError }) {
  const [cuentas, setCuentas] = useState([]);
  const [cuentaId, setCuentaId] = useState('');
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [saving, setSaving] = useState(false);
  const [comprobanteUrl, setComprobanteUrl] = useState('');
  const [comprobanteNombre, setComprobanteNombre] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [ocr, setOcr] = useState(null);              // {ok, monto, referencia, cuenta_destino_texto, cuenta_destino_match}
  const [analizandoOCR, setAnalizandoOCR] = useState(false);
  const [cuentaAutoDetectada, setCuentaAutoDetectada] = useState(null);

  useEffect(() => {
    if (!open || !empresaId) return;
    (async () => {
      const headers = await getAuthHeaders();
      fetch(`${API_BASE}/turismo/cuentas-destino-pago?empresa_id=${empresaId}`, { headers })
        .then(r => r.ok ? r.json() : [])
        .then(data => setCuentas(Array.isArray(data) ? data.filter(c => c.activo !== false) : []))
        .catch(() => setCuentas([]));
    })();
  }, [open, empresaId]);

  useEffect(() => {
    if (open && charter) {
      setMonto(String(charter.saldo_pendiente || ''));
      setCuentaId('');
      setReferencia('');
      setComprobanteUrl('');
      setComprobanteNombre('');
      setOcr(null);
      setCuentaAutoDetectada(null);
    }
  }, [open, charter]);

  const analizarOCR = async (url) => {
    setAnalizandoOCR(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/soportes/analizar-comprobante`, {
        method: 'POST', headers: await getAuthHeaders(),
        body: JSON.stringify({
          url,
          monto_esperado: Number(monto) || 0,
          empresa_id: empresaId,
        }),
      });
      const d = await r.json();
      setOcr(d);
      // Prellenar referencia si vacía
      if (d.ok && d.referencia && !referencia.trim()) setReferencia(d.referencia);
      // Auto-seleccionar cuenta si el backend hizo match único con las cuentas
      // configuradas. El operador puede cambiarla — es sólo pre-selección.
      const match = d?.cuenta_destino_match;
      if (match?.cuenta_id && cuentas.some(c => c.id === match.cuenta_id)) {
        setCuentaId(match.cuenta_id);
        setCuentaAutoDetectada(match.cuenta_id);
      }
    } catch (err) {
      setOcr({ ok: false, error: 'No se pudo analizar' });
    }
    setAnalizandoOCR(false);
  };

  const subirComprobante = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    setOcr(null);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const t = localStorage.getItem('token');
      const r = await fetch(`${API_BASE}/turismo/soportes/upload`, {
        method: 'POST',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        body: fd,
      });
      if (!r.ok) throw new Error('Error subiendo comprobante');
      const d = await r.json();
      setComprobanteUrl(d.url);
      setComprobanteNombre(file.name);
      // Disparar OCR + match de cuenta destino automáticamente
      analizarOCR(d.url);
    } catch (err) { onError?.(err.message); }
    finally { setSubiendo(false); }
  };

  if (!charter) return null;

  const tipoAMetodo = (tipo) => {
    const map = { efectivo: 'efectivo', transferencia: 'transferencia',
                  datafono: 'tarjeta', tarjeta: 'tarjeta',
                  bold: 'bold_link', epayco: 'epayco_link' };
    return map[(tipo || '').toLowerCase()] || 'transferencia';
  };

  const cuentaSel = cuentas.find(c => c.id === Number(cuentaId));
  const puedeCobrar = cuentaId && Number(monto) > 0
    && Number(monto) <= (charter.saldo_pendiente + 0.01);

  const submit = async () => {
    if (!puedeCobrar || saving) return;
    setSaving(true);
    try {
      const body = {
        metodo_pago: tipoAMetodo(cuentaSel?.tipo),
        monto: Number(monto),
        referencia: referencia.trim() || null,
        cuenta_destino_id: cuentaSel?.id || null,
        comprobante_url: comprobanteUrl || null,
      };
      const r = await fetch(
        `${API_BASE}/turismo/muelle/reservas/${charter.id}/cobrar`,
        { method: 'PATCH', headers: await getAuthHeaders(), body: JSON.stringify(body) }
      );
      const data = await r.json();
      if (!r.ok) {
        onError?.(data.detail || 'Error al cobrar');
        return;
      }
      onSuccess?.(`Cobrado ${Number(monto).toLocaleString('es-CO')} · saldo restante $${Number(data.saldo_pendiente).toLocaleString('es-CO')}`);
    } catch (e) {
      onError?.('Error de red');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaymentIcon sx={{ color: '#16a34a' }} />
        Registrar cobro
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>
          Charter <strong>{charter.codigo_reserva}</strong> · Saldo pendiente:{' '}
          <strong style={{ color: '#dc2626' }}>{fmtMoney(charter.saldo_pendiente)}</strong>
        </Typography>
        <TextField
          fullWidth size="small" select label="Cuenta destino *"
          value={cuentaId}
          onChange={(e) => {
            setCuentaId(e.target.value);
            // Si el operador elige distinto a lo detectado, quitamos el chip
            if (String(cuentaAutoDetectada) !== String(e.target.value)) {
              setCuentaAutoDetectada(null);
            }
          }}
          sx={{ mb: cuentaAutoDetectada ? 0.5 : 2 }}
        >
          {cuentas.length === 0 && (
            <MenuItem value="" disabled>Sin cuentas configuradas</MenuItem>
          )}
          {cuentas.map(c => (
            <MenuItem key={c.id} value={c.id}>
              {c.nombre} <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 6 }}>· {c.tipo}</span>
            </MenuItem>
          ))}
        </TextField>
        {cuentaAutoDetectada && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="✨ Detectada por Claude Vision · editable" size="small"
              sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', fontWeight: 700, fontSize: 10, height: 20 }}
            />
          </Box>
        )}
        <TextField
          fullWidth size="small" type="number" label="Monto a cobrar *"
          value={monto} onChange={(e) => setMonto(e.target.value)}
          inputProps={{ min: 0, max: charter.saldo_pendiente, step: 1000 }}
          helperText={`Máximo ${fmtMoney(charter.saldo_pendiente)}`}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth size="small" label="Referencia (opcional)"
          value={referencia} onChange={(e) => setReferencia(e.target.value)}
          placeholder="# transferencia, autorización datáfono, etc."
          sx={{ mb: 2 }}
        />
        {/* Comprobante (imagen/PDF) */}
        <Box sx={{ border: '1px dashed #cbd5e1', borderRadius: 2, p: 1.5, bgcolor: '#f8fafc' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Comprobante (opcional)
          </Typography>
          {comprobanteUrl ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptLongIcon sx={{ color: '#16a34a' }} />
                <Typography sx={{ fontSize: 12, flex: 1, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {comprobanteNombre}
                </Typography>
                <Button size="small"
                  onClick={() => {
                    setComprobanteUrl(''); setComprobanteNombre('');
                    setOcr(null); setCuentaAutoDetectada(null);
                  }}
                  sx={{ textTransform: 'none', color: '#dc2626', fontSize: 11 }}>
                  Quitar
                </Button>
              </Box>
              {analizandoOCR && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={12} sx={{ color: '#7c3aed' }} />
                  <Typography sx={{ fontSize: 11, color: '#7c3aed' }}>
                    Claude Vision analizando comprobante…
                  </Typography>
                </Box>
              )}
              {ocr?.cuenta_destino_texto && (
                <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontSize: 10.5, color: '#475569' }}>
                    <strong>Cuenta detectada en voucher:</strong> "{ocr.cuenta_destino_texto}"
                  </Typography>
                  {ocr.cuenta_destino_match?.cuenta_id && (
                    <Typography sx={{ fontSize: 10.5, color: '#7c3aed', fontWeight: 700, mt: 0.25 }}>
                      ✨ Match: {ocr.cuenta_destino_match.cuenta_nombre}
                      {' '}(score {(ocr.cuenta_destino_match.score * 100).toFixed(0)}%) · pre-seleccionada
                    </Typography>
                  )}
                  {!ocr.cuenta_destino_match?.cuenta_id
                    && ocr.cuenta_destino_match?.candidatos?.length > 0 && (
                    <Typography sx={{ fontSize: 10.5, color: '#92400e', mt: 0.25 }}>
                      ⚠ Ambiguo. Posibles: {ocr.cuenta_destino_match.candidatos
                        .map(x => `${x.nombre} (${(x.score * 100).toFixed(0)}%)`).join(', ')}
                    </Typography>
                  )}
                  {!ocr.cuenta_destino_match?.cuenta_id
                    && (!ocr.cuenta_destino_match?.candidatos?.length) && (
                    <Typography sx={{ fontSize: 10.5, color: '#991b1b', mt: 0.25 }}>
                      ⛔ No coincide con cuentas configuradas — elige manualmente arriba.
                    </Typography>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Button component="label" fullWidth variant="outlined" size="small"
              disabled={subiendo}
              sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#475569' }}
            >
              {subiendo ? 'Subiendo…' : 'Adjuntar imagen o PDF (auto-detecta cuenta)'}
              <input type="file" hidden accept="image/*,application/pdf"
                onChange={subirComprobante} />
            </Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!puedeCobrar || saving}
          sx={{
            textTransform: 'none', fontWeight: 700,
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #15803d 0%, #14532d 100%)' },
          }}
        >
          {saving ? 'Cobrando…' : 'Registrar cobro'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Row({ label, value, valueColor }) {
  return (
    <Box sx={{ display: 'flex', mb: 0.75 }}>
      <Typography sx={{ fontSize: 12, color: '#94a3b8', minWidth: 110, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: valueColor || '#0f172a', fontWeight: 500 }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

/**
 * buildWhatsAppMessage — arma el texto pre-formateado para el assisted-send.
 * Fernando pulsa el botón, WhatsApp abre con el mensaje listo, sólo debe
 * apretar "Enviar". No usa Twilio (evita mezclar el número compartido con
 * Entretur — regla de negocio confirmada por el usuario).
 */
function buildWhatsAppMessage(c, empresaId) {
  const link = `${window.location.origin}/v/${c.codigo_reserva}`;
  const fecha = c.fecha_alquiler ? (() => {
    const [y, m, d] = c.fecha_alquiler.split('-');
    return `${d}/${m}/${y}`;
  })() : '—';
  const saldo = Number(c.saldo_pendiente || 0);
  const saldoTexto = saldo > 0
    ? `Saldo pendiente: $${saldo.toLocaleString('es-CO')}`
    : `✅ Charter totalmente pagado`;
  return [
    `Hola ${c.cliente?.nombre || ''} 👋`,
    ``,
    `Confirmamos tu *charter* con nosotros:`,
    ``,
    `📋 Código: *${c.codigo_reserva}*`,
    `🚤 Embarcación: ${c.embarcacion_nombre || '—'}`,
    `📅 Fecha: ${fecha}`,
    `⏰ Zarpe: ${c.hora_zarpe || 'por confirmar'}${c.hora_regreso ? ` · Regreso: ${c.hora_regreso}` : ''}`,
    `👥 Grupo: ${c.adultos || 0} adultos${c.ninos ? ` · ${c.ninos} niños` : ''}${c.bebes ? ` · ${c.bebes} bebés` : ''}`,
    ``,
    `💰 Valor total: $${Number(c.precio_total || 0).toLocaleString('es-CO')}`,
    saldoTexto,
    ``,
    `📄 Comprobante: ${link}`,
    ``,
    `Cualquier duda por acá. ¡Gracias!`,
  ].join('\n');
}

/**
 * EditarCharterDialog — edita fecha, horas, pax, embarcación, cliente y notas.
 * Usa PATCH /turismo/charter/reservas/{id}. El backend re-valida overbooking
 * si cambia fecha o embarcación y estado ∈ {confirmada, embarcada}.
 */
function EditarCharterDialog({ open, charter, embarcaciones, puntos, empresaId, onClose, onSuccess, onError }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dispBulk, setDispBulk] = useState({});

  useEffect(() => {
    if (open && charter) {
      setForm({
        fecha_alquiler: charter.fecha_alquiler || '',
        hora_zarpe: charter.hora_zarpe || '',
        hora_regreso: charter.hora_regreso || '',
        embarcacion_id: charter.embarcacion_id || '',
        punto_embarque_id: charter.punto_embarque_id || '',
        adultos: charter.adultos || 1,
        ninos: charter.ninos || 0,
        bebes: charter.bebes || 0,
        cliente_nombre: charter.cliente?.nombre || '',
        cliente_whatsapp: charter.cliente?.whatsapp || '',
        cliente_correo: charter.cliente?.correo || '',
        cliente_identificacion: charter.cliente?.identificacion || '',
        descuento_comercial: charter.descuento_comercial || '',
        descuento_comercial_motivo: charter.descuento_comercial_motivo || '',
        descuento_tipo: 'valor',   // al abrir, el existente siempre viene en pesos
        notas: charter.notas || '',
      });
    }
  }, [open, charter]);

  // Bulk disponibilidad al cambiar la fecha
  useEffect(() => {
    if (!open || !form.fecha_alquiler || !empresaId) { setDispBulk({}); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `${API_BASE}/turismo/charter/disponibilidad/dia?fecha=${form.fecha_alquiler}&empresa_id=${empresaId}&excluir_reserva_id=${charter?.id || ''}`,
          { headers: await getAuthHeaders() }
        );
        if (r.ok) {
          const data = await r.json();
          const map = {};
          (Array.isArray(data) ? data : []).forEach(d => {
            map[d.embarcacion_id] = { disponible: d.disponible, motivo: d.motivo };
          });
          setDispBulk(map);
        }
      } catch (e) { console.error('[disp bulk edit]', e); }
    }, 300);
    return () => clearTimeout(t);
  }, [open, form.fecha_alquiler, empresaId, charter?.id]);

  if (!charter) return null;

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const body = {
        fecha_alquiler: form.fecha_alquiler || null,
        hora_zarpe: form.hora_zarpe || null,
        hora_regreso: form.hora_regreso || null,
        embarcacion_id: Number(form.embarcacion_id) || null,
        punto_embarque_id: form.punto_embarque_id ? Number(form.punto_embarque_id) : null,
        adultos: Number(form.adultos) || 1,
        ninos: Number(form.ninos) || 0,
        bebes: Number(form.bebes) || 0,
        cliente_nombre: (form.cliente_nombre || '').trim() || null,
        cliente_whatsapp: (form.cliente_whatsapp || '').trim() || null,
        cliente_correo: (form.cliente_correo || '').trim() || null,
        cliente_identificacion: (form.cliente_identificacion || '').trim() || null,
        // Si el usuario eligió porcentaje, convertimos a pesos antes de enviar
        // (la BD sólo guarda pesos absolutos). Capamos al precio del catálogo.
        descuento_comercial: (() => {
          const catalogo = Number(charter.precio_base_catalogo || 0);
          const input = Number(form.descuento_comercial) || 0;
          if (input <= 0) return 0;
          const enPesos = form.descuento_tipo === 'porcentaje'
            ? Math.round((catalogo * Math.min(input, 100)) / 100)
            : input;
          return Math.max(0, Math.min(enPesos, catalogo));
        })(),
        descuento_comercial_motivo: Number(form.descuento_comercial) > 0
          ? (form.descuento_comercial_motivo || '').trim() || null
          : null,
        notas: (form.notas || '').trim() || null,
      };
      const r = await fetch(
        `${API_BASE}/turismo/charter/reservas/${charter.id}?empresa_id=${empresaId}`,
        { method: 'PATCH', headers: await getAuthHeaders(), body: JSON.stringify(body) }
      );
      const data = await r.json();
      if (!r.ok) {
        const msg = data?.detail?.message || data?.detail || 'Error al editar';
        onError?.(typeof msg === 'string' ? msg : JSON.stringify(msg));
        return;
      }
      onSuccess?.('Charter actualizado');
    } catch (e) {
      onError?.('Error de red');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditIcon sx={{ color: '#7c3aed' }} />
        Editar charter {charter.codigo_reserva}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', mt: 1, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Servicio
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="date" label="Fecha"
              InputLabelProps={{ shrink: true }}
              value={form.fecha_alquiler || ''} onChange={set('fecha_alquiler')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" select label="Embarcación"
              value={form.embarcacion_id || ''} onChange={set('embarcacion_id')}>
              {embarcaciones.map(e => {
                const d = dispBulk[e.id];
                return (
                  <MenuItem key={e.id} value={e.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>{e.nombre} · {e.capacidad_total} pax</Box>
                      {form.fecha_alquiler && d && (
                        <Chip size="small"
                          label={d.disponible ? 'Disponible' : (d.motivo?.tipo === 'pasadia' ? 'Pasadía' : 'Charter')}
                          sx={{
                            height: 18, fontSize: 9.5, fontWeight: 700,
                            bgcolor: d.disponible ? '#dcfce7' : (d.motivo?.tipo === 'pasadia' ? '#dbeafe' : '#ede9fe'),
                            color: d.disponible ? '#166534' : (d.motivo?.tipo === 'pasadia' ? '#1e40af' : '#5b21b6'),
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" select label="Punto de embarque"
              value={form.punto_embarque_id || ''} onChange={set('punto_embarque_id')}>
              <MenuItem value=""><em>Sin definir</em></MenuItem>
              {(puntos || []).map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nombre}{p.direccion ? ` · ${p.direccion}` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="time" label="Zarpe"
              InputLabelProps={{ shrink: true }}
              value={form.hora_zarpe || ''} onChange={set('hora_zarpe')} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="time" label="Regreso"
              InputLabelProps={{ shrink: true }}
              value={form.hora_regreso || ''} onChange={set('hora_regreso')} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Adultos"
              inputProps={{ min: 1, max: 100 }}
              value={form.adultos || 1} onChange={set('adultos')} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Niños"
              inputProps={{ min: 0, max: 100 }}
              value={form.ninos || 0} onChange={set('ninos')} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Bebés"
              inputProps={{ min: 0, max: 100 }}
              value={form.bebes || 0} onChange={set('bebes')} />
          </Grid>
        </Grid>

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', mt: 2, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Cliente
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Nombre"
              value={form.cliente_nombre || ''} onChange={set('cliente_nombre')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="WhatsApp"
              value={form.cliente_whatsapp || ''} onChange={set('cliente_whatsapp')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Correo"
              value={form.cliente_correo || ''} onChange={set('cliente_correo')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Identificación"
              value={form.cliente_identificacion || ''} onChange={set('cliente_identificacion')} />
          </Grid>
        </Grid>

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', mt: 2, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Descuento comercial
        </Typography>
        {(() => {
          // Descuento aceptando valor o porcentaje. Calculamos en pesos para
          // guardar en BD y para mostrar precio final.
          const catalogo = Number(charter.precio_base_catalogo || 0);
          const input = Number(form.descuento_comercial) || 0;
          const enPesos = form.descuento_tipo === 'porcentaje'
            ? Math.round((catalogo * Math.min(input, 100)) / 100)
            : input;
          const descAplicado = Math.max(0, Math.min(enPesos, catalogo));
          const precioFinal = Math.max(catalogo - descAplicado, 0);
          const excedeMax = form.descuento_tipo === 'porcentaje'
            ? input > 100 : input > catalogo;
          return (
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField select size="small" label="Tipo"
                    value={form.descuento_tipo || 'valor'} onChange={set('descuento_tipo')}
                    sx={{ minWidth: 120 }}>
                    <MenuItem value="valor">Valor ($)</MenuItem>
                    <MenuItem value="porcentaje">Porcentaje (%)</MenuItem>
                  </TextField>
                  <TextField fullWidth size="small" type="number" label="Descuento"
                    inputProps={form.descuento_tipo === 'porcentaje'
                      ? { min: 0, max: 100, step: 1 }
                      : { min: 0, max: catalogo, step: 10000 }}
                    value={form.descuento_comercial || ''} onChange={set('descuento_comercial')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">
                        {form.descuento_tipo === 'porcentaje' ? '%' : '$'}
                      </InputAdornment>,
                    }}
                    error={excedeMax}
                    helperText={input > 0
                      ? `${form.descuento_tipo === 'porcentaje' ? `${input}% = ` : ''}${fmtMoney(descAplicado)} · Precio final: ${fmtMoney(precioFinal)}`
                      : 'Sin descuento'}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Motivo"
                  value={form.descuento_comercial_motivo || ''}
                  onChange={set('descuento_comercial_motivo')}
                  required={input > 0}
                  error={input > 0 && (form.descuento_comercial_motivo || '').trim().length < 3}
                  helperText={input > 0 && (form.descuento_comercial_motivo || '').trim().length < 3
                    ? 'Motivo requerido' : ' '}
                />
              </Grid>
            </Grid>
          );
        })()}

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', mt: 2, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Notas
        </Typography>
        <TextField fullWidth size="small" multiline rows={2}
          value={form.notas || ''} onChange={set('notas')} />

        {['confirmada', 'embarcada'].includes(charter.estado) && (
          <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
            Charter en estado <strong>{charter.estado}</strong>: si cambias fecha o embarcación
            se re-valida disponibilidad. Rechazo 409 si otro reservó primero.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving}
          sx={{
            textTransform: 'none', fontWeight: 700,
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)' },
          }}
        >
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * TimelineHistorial — línea de tiempo vertical de eventos de auditoría.
 * Recibe los items del endpoint /turismo/auditoria filtrados por este charter.
 * Colorea por tipo de acción; el más reciente arriba.
 */
const ACCION_STYLES = {
  charter_creado_presupuesto: { color: '#f59e0b', label: 'Presupuesto creado' },
  charter_creado_confirmada:  { color: '#1e40af', label: 'Creado y confirmado' },
  charter_editado:            { color: '#64748b', label: 'Editado' },
  charter_confirmada:         { color: '#1e40af', label: 'Confirmado' },
  charter_embarcada:          { color: '#166534', label: 'Embarcado' },
  charter_completada:         { color: '#334155', label: 'Completado' },
  charter_cancelada:          { color: '#991b1b', label: 'Cancelado' },
  saldo_cobrado_muelle:       { color: '#059669', label: 'Cobro registrado' },
};

function TimelineHistorial({ eventos }) {
  // Los eventos vienen del backend en orden desc (más reciente primero) — así los mostramos
  return (
    <Box sx={{ position: 'relative', pl: 3 }}>
      {/* Línea vertical */}
      <Box sx={{
        position: 'absolute', left: 8, top: 8, bottom: 8,
        width: 2, bgcolor: '#e2e8f0',
      }} />
      {eventos.map((ev, i) => {
        const s = ACCION_STYLES[ev.accion] || { color: '#64748b', label: ev.accion };
        return (
          <Box key={ev.id} sx={{ position: 'relative', mb: i === eventos.length - 1 ? 0 : 2 }}>
            {/* Punto en la línea */}
            <Box sx={{
              position: 'absolute', left: -20, top: 4,
              width: 12, height: 12, borderRadius: '50%',
              bgcolor: s.color, border: '2px solid #fff',
              boxShadow: `0 0 0 2px ${s.color}55`,
            }} />
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: s.color }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                {ev.created_at ? new Date(ev.created_at).toLocaleString('es-CO',
                  { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }
                ) : ''}
              </Typography>
              {ev.usuario_nombre && (
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  · {ev.usuario_nombre}
                </Typography>
              )}
            </Box>
            {ev.descripcion && (
              <Typography sx={{ fontSize: 12, color: '#475569', whiteSpace: 'pre-wrap' }}>
                {ev.descripcion}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
